import { useState, type KeyboardEvent } from 'react';
import type { WsIs, WsOverview, WsTur } from './api';
import { WS } from './metin/web-sitesi';
import { AsamaBloklari, AsamaTablosu, BilgiSimgesi, DonenKareleri, DonusumSvg, DonusumTablosu, NoktaSeridi, NoktaTablosu, RiskLejandi, RiskSvg, RiskTablosu, Spark, UyariSimgesi } from './WebSitesiGrafik';
import { Dt, OlcumYok, VizKart } from './WebSitesiOrtak';
import { yasMetni } from './web-sitesi-bicim';

/**
 * Genel bakış (taslak A, 07.10.2026). Her sayı ve grafik ilgili sekmeyi süzülmüş açar:
 * `git(hedef)` ile aynı hedef metinleri `web-sitesi-durum.ts` içinde.
 */

function tusla(islem: () => void) {
  return (olay: KeyboardEvent) => {
    if (olay.key === 'Enter' || olay.key === ' ') {
      olay.preventDefault();
      islem();
    }
  };
}

/** Yaş çubuğu: eşik çizgisi kırmızı, eşiği aşınca çubuk kırmızı. */
export function YasCubugu({ yas, esik }: { yas: number; esik: number }) {
  const w = 84;
  const x = (Math.min(yas, 40) / 40) * w;
  const tx = (Math.min(esik, 40) / 40) * w;
  return (
    <svg width={w} height={12} viewBox={`0 0 ${w} 12`} aria-hidden="true">
      <rect x={0} y={5} width={w} height={2} fill="var(--mute-soft)" />
      <rect x={tx} y={2} width={1} height={8} fill="var(--danger)" />
      <rect x={0} y={3.5} width={Math.max(3, x)} height={5} rx={2} fill={yas > esik ? 'var(--danger)' : 'var(--accent)'} />
    </svg>
  );
}

export function GecikmeEtiketi({ yas, esik }: { yas: number; esik: number }) {
  return yas > esik ? (
    <span className="late">
      <UyariSimgesi />
      {WS.esikAsildi(esik)}
    </span>
  ) : null;
}

function SaglikSeridi({ s, git }: { s: WsOverview['health']; git: (h: string) => void }) {
  const [acik, setAcik] = useState(false);
  const fark = s.orders.v3 !== null ? s.orders.v3 - s.orders.crm : null;
  const en = Math.max(1, s.orders.v3 ?? s.orders.crm);
  return (
    <section className="health" aria-label={WS.saglik.baslik} data-testid="ws-saglik">
      <div className="health-row">
        <strong>{WS.saglik.baslik}</strong>
        <span className="health-item" style={{ color: 'var(--ink-soft)' }}>
          <BilgiSimgesi />
          <button type="button" className="link-btn" title={WS.saglik.sistemAc} data-testid="ws-saglik-sistem" onClick={() => git('talepler:sistem')}>{WS.saglik.sistem(s.hiddenSystemMessages)}</button>
        </span>
        <span className="health-item" style={{ color: 'var(--ink-soft)' }}>
          <BilgiSimgesi />
          <button type="button" className="link-btn" title={WS.saglik.spamAc} data-testid="ws-saglik-spam" onClick={() => git('talepler:spam')}>{WS.saglik.spam(s.hiddenSpam)}</button>
        </span>
        <span className="health-item" style={{ color: fark !== null && fark !== 0 ? 'var(--warn)' : undefined }}>
          {fark === null || fark !== 0 ? <UyariSimgesi /> : <BilgiSimgesi />}
          <span>{WS.saglik.siparis(s.orders.v3, s.orders.crm)}</span>
        </span>
        <span className="health-item" style={{ color: 'var(--warn)' }}>
          <UyariSimgesi />
          <span>{WS.saglik.vize(s.visa.crm)}</span>
        </span>
        <span className="grow" />
        <button type="button" className="link-btn" aria-expanded={acik} data-testid="ws-saglik-ayrinti" onClick={() => setAcik((a) => !a)}>
          {acik ? WS.saglik.gizle : WS.saglik.ayrinti}
        </button>
      </div>
      {acik ? (
        <div className="health-detail" data-testid="ws-saglik-detay">
          <div className="hd">
            <h4>{WS.saglik.siparisBaslik}</h4>
            {s.orders.v3 !== null ? (
              <div className="pair">
                <span>{WS.saglik.v3Etiket}</span>
                <i className="a" style={{ width: `${(s.orders.v3 / en) * 100}%` }} />
                <span>{s.orders.v3}</span>
              </div>
            ) : null}
            <div className="pair">
              <span>{WS.saglik.crmEtiket}</span>
              <i style={{ width: `${(s.orders.crm / en) * 100}%` }} />
              <span>{s.orders.crm}</span>
            </div>
            <p>{fark === null ? WS.saglik.siparisOkunamadi : fark === 0 ? WS.saglik.siparisEsit : WS.saglik.siparisFark(fark)}</p>
          </div>
          <div className="hd">
            <h4>{WS.saglik.vizeBaslik}</h4>
            <div className="pair">
              <span>{WS.saglik.crmEtiket}</span>
              <i style={{ width: s.visa.crm === 0 ? '2px' : '100%' }} />
              <span>{s.visa.crm}</span>
            </div>
            <p>{WS.saglik.vizeNot(s.visa.crm)}</p>
          </div>
          <div className="hd">
            <h4>{WS.saglik.destekBaslik}</h4>
            <div className="pair">
              <span>{WS.saglik.gercekEtiket}</span>
              <i className="a" style={{ width: `${(s.support.real / Math.max(1, s.support.real + s.support.system + s.support.spam)) * 100}%` }} />
              <span>{s.support.real}</span>
            </div>
            <div className="pair">
              <span>{WS.saglik.sistemEtiket}</span>
              <i style={{ width: `${(s.support.system / Math.max(1, s.support.real + s.support.system + s.support.spam)) * 100}%` }} />
              <span>{s.support.system}</span>
            </div>
            <div className="pair">
              <span>{WS.saglik.spamEtiket}</span>
              <i style={{ width: `${(s.support.spam / Math.max(1, s.support.real + s.support.system + s.support.spam)) * 100}%` }} />
              <span>{s.support.spam}</span>
            </div>
            <p>{WS.saglik.destekNot(s.support.real + s.support.system + s.support.spam, s.support.system, s.support.spam)}</p>
          </div>
          <div className="hd">
            <h4>{WS.saglik.gorulmeBaslik}</h4>
            <p>{s.lastSeen.delivered === 0 ? WS.saglik.gorulmeHic : WS.saglik.gorulmeNot(s.lastSeen.delivered, s.lastSeen.total, s.consent.filled)}</p>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function Karo({ ad, kutu, hedef, git, alt }: { ad: string; kutu: { today: number; last7: number; previous7: number; daily: number[] }; hedef: string; git: (h: string) => void; alt: Date }) {
  return (
    <article className="card tile" role="button" tabIndex={0} data-testid={`ws-karo-${hedef}`} onClick={() => git(hedef)} onKeyDown={tusla(() => git(hedef))} style={{ cursor: 'pointer' }}>
      <div className="lab">{ad}</div>
      <div className="val">
        <span className="num">{kutu.today}</span>
        <span className="unit">{WS.kutu.bugun}</span>
      </div>
      <p className="cmp">{WS.kutu.karsilastir(kutu.last7, kutu.previous7)}</p>
      <div className="spark">
        <Spark kutu={kutu} aria={WS.kutu.gunAria(ad)} bugun={alt} />
      </div>
    </article>
  );
}

export function Genel({ o, git, ac, secili }: { o: WsOverview; git: (h: string) => void; ac: (i: WsIs) => void; secili: string | null }) {
  const simdi = new Date(o.measuredAt);
  const th: Record<WsTur, number> = { destek: o.thresholds.SUPPORT, on: o.thresholds.DEMAND, vize: o.thresholds.VISA, sip: o.thresholds.ORDER };
  const donen = o.boxes.donen;
  const stageAc = (id: WsTur, key: string) => git(`stage:${id}:${key}`);
  const riskBoyut = o.risk.seen.length + o.risk.never.length;
  return (
    <>
      <SaglikSeridi s={o.health} git={git} />
      <div className="tiles" data-testid="ws-karolar">
        <Karo ad={WS.kutu.uye} kutu={o.boxes.uye} hedef="uyeler:reg" git={git} alt={simdi} />
        <Karo ad={WS.kutu.destek} kutu={o.boxes.destek} hedef="talepler:destek" git={git} alt={simdi} />
        <Karo ad={WS.kutu.onVize} kutu={o.boxes.onVize} hedef="talepler:onvize" git={git} alt={simdi} />
        <Karo ad={WS.kutu.siparis} kutu={o.boxes.siparis} hedef="siparis:hepsi" git={git} alt={simdi} />
        <article className="card tile" role="button" tabIndex={0} data-testid="ws-karo-uyeler:seen" onClick={() => git('uyeler:seen')} onKeyDown={tusla(() => git('uyeler:seen'))} style={{ cursor: 'pointer' }}>
          <div className="lab">{WS.kutu.donen}</div>
          {donen.known === 0 ? (
            <>
              <div className="val">
                <span className="num" style={{ fontSize: '1.4rem' }}>{WS.kutu.donenBilinmiyor}</span>
              </div>
              <p className="cmp">{WS.kutu.donenBilinmiyorNot}</p>
            </>
          ) : (
            <>
              <div className="val">
                <span className="num">{donen.seen7}</span>
                <span className="unit">{WS.kutu.son7Birim}</span>
              </div>
              <p className="cmp">{WS.kutu.donenKarsilastir(donen.seen30, donen.total, donen.never)}</p>
              <div className="spark">
                <DonenKareleri donen={donen} />
              </div>
            </>
          )}
        </article>
      </div>
      <div>
        <p className="keynote">
          <span className="key"><i style={{ background: 'var(--accent)' }} />{WS.kutu.anahtarSon7}</span>
          <span className="key"><i style={{ background: 'var(--mute-soft)' }} />{WS.kutu.anahtarOnceki7}</span>
          <span>{WS.kutu.anahtarNot}</span>
        </p>
        <p className="keynote">
          <span className="go">{WS.kutu.tikla}</span>
        </p>
      </div>
      <div className="sumbar" data-testid="ws-ozet">
        <strong>{WS.ozet.bekliyor(o.summary.open)}</strong>
        <button type="button" className="chip" data-testid="ws-ozet-sende" onClick={() => git('sende')}>{WS.ozet.sende(o.summary.mine)}</button>
        <button type="button" className="chip" data-testid="ws-ozet-must" onClick={() => git('must')}>{WS.ozet.musteride(o.summary.pendingPayment)}</button>
        <span className="count-badge warnbadge">{WS.ozet.asti(o.summary.overdue)}</span>
        <span className="grow" />
        <span className="cnote" style={{ margin: 0 }}>{WS.ozet.esikler(o.thresholds.SUPPORT, o.thresholds.DEMAND, o.thresholds.ORDER, o.thresholds.VISA)}</span>
      </div>
      <div className="a-row r1">
        <VizKart
          id="v-age"
          baslik={WS.yas.baslik}
          alt={WS.yas.alt(o.items.length)}
          grafik={(w) => <NoktaSeridi genislik={w} isler={o.items} esikler={th} secili={secili} ac={ac} />}
          tablo={() => <NoktaTablosu isler={o.items} />}
          altEk={
            <div className="legend">
              <span><svg width="14" height="14"><circle cx="7" cy="7" r="5" fill="var(--accent)" stroke="var(--surface)" strokeWidth="2" /></svg>{WS.yas.icinde}</span>
              <span><svg width="14" height="14"><circle cx="7" cy="7" r="5" fill="var(--danger)" stroke="var(--surface)" strokeWidth="2" /></svg>{WS.yas.asti}</span>
            </div>
          }
        />
        <VizKart id="v-stage" baslik={WS.asama.baslik} alt={WS.asama.alt} grafik={(w) => <AsamaBloklari genislik={w} asamalar={o.stages} kolon={w >= 460 ? 2 : 1} ac={stageAc} />} tablo={() => <AsamaTablosu asamalar={o.stages} />} />
      </div>
      <div className="a-row r2">
        <VizKart
          id="v-conv"
          sinif="a-conv"
          baslik={WS.donusum.baslik}
          alt={WS.donusum.alt}
          grafik={(w) => (
            <>
              <div className="hero">
                <span className="big">{o.orderHero.paid}</span>
                <div>
                  <div className="what">{WS.donusum.odenenEtiket}</div>
                  <p>{o.orderHero.sentence}</p>
                </div>
              </div>
              <DonusumSvg genislik={w} adimlar={o.funnel} ac={(i) => git(`funnel:${i}`)} />
              <p className="cnote">{WS.donusum.notu}</p>
            </>
          )}
          tablo={() => <DonusumTablosu adimlar={o.funnel} />}
          altEk={<OlcumYok anahtarlar={['zi', 'ts']} />}
        />
        <VizKart
          id="v-risk"
          baslik={WS.risk.baslik}
          alt={WS.risk.alt(o.risk.total)}
          grafik={(w) =>
            o.risk.seenTotal + o.risk.neverTotal === 0 ? (
              <div className="empty-state" data-testid="ws-risk-bilinmiyor">
                <b>{WS.risk.bilinmiyor}</b>
                {WS.risk.bilinmiyorNot}
              </div>
            ) : (
              <>
                <RiskSvg genislik={w} risk={o.risk} ac={(id) => git(`person:${id}`)} />
                {o.risk.seenTotal > o.risk.seen.length || o.risk.neverTotal > o.risk.never.length ? (
                  <p className="cnote">{WS.risk.daha(o.risk.seenTotal + o.risk.neverTotal - riskBoyut)}</p>
                ) : null}
              </>
            )
          }
          tablo={() => <RiskTablosu risk={o.risk} />}
          altEk={
            <>
              <RiskLejandi />
              <div className="riskcount" data-testid="ws-risk-sayaclar">
                {o.risk.counters.map((r) => (
                  <button key={r.code} type="button" className="rc" data-testid={`ws-${r.code}`} onClick={() => (r.memberExternalId ? git(`person:${r.memberExternalId}`) : git(r.code))}>
                    <b>{r.count}</b>
                    <span>{r.label}</span>
                  </button>
                ))}
              </div>
            </>
          }
        />
      </div>
      <section style={{ marginTop: 'var(--space-lg)' }}>
        <OlcumYok anahtarlar={['src', 'ulke', 'yanit']} />
      </section>
      <section className="card" style={{ marginTop: 'var(--space-lg)' }} data-testid="ws-en-uzun">
        <div className="card-head">
          <div>
            <h3>{WS.enUzun.baslik}</h3>
            <p className="sub">{WS.enUzun.alt}</p>
          </div>
          <button type="button" className="btn sm" onClick={() => git('sende')}>{WS.enUzun.tumu(o.summary.open)}</button>
        </div>
        <Dt
          kolonlar={[WS.enUzun.kim, WS.enUzun.ne, WS.enUzun.tur, WS.enUzun.bekleme]}
          satirlar={o.oldest.map((i) => ({
            key: i.key,
            gec: i.overdue,
            ac: () => ac(i),
            hucreler: [
              <div className="who" key="k"><b>{i.who}</b><span>{i.shortReference}</span></div>,
              i.what,
              <span className="tag" key="t">{WS.tur[i.tur]}</span>,
              <div key="b"><div className="age"><b>{yasMetni(i.ageDays)}</b><YasCubugu yas={i.ageDays} esik={i.threshold} /></div><GecikmeEtiketi yas={i.ageDays} esik={i.threshold} /></div>,
            ],
          }))}
        />
      </section>
    </>
  );
}
